/**
 * AddScreeningModal Component
 * 
 * Modal form for adding/editing HR screening questions
 */

import React, { useEffect } from 'react';
import { Modal, Form, Input, Button } from 'antd';
import { ManualScreeningQuestionState } from '../utils/formTransformers';
import { validationRules } from '../utils/validators';

const { TextArea } = Input;

interface AddScreeningModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (question: ManualScreeningQuestionState) => void;
  editingQuestion?: ManualScreeningQuestionState | null;
}

const AddScreeningModal: React.FC<AddScreeningModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  editingQuestion,
}) => {
  const [form] = Form.useForm();
  const isEditing = !!editingQuestion;

  // Reset form when modal opens/closes or editing question changes
  useEffect(() => {
    if (visible) {
      if (editingQuestion) {
        form.setFieldsValue({
          description: editingQuestion.description,
        });
      } else {
        form.resetFields();
      }
    } else {
      form.resetFields();
    }
  }, [visible, editingQuestion, form]);

  // Generate unique ID with high entropy to prevent collisions
  const generateUniqueId = (): string => {
    const timestamp = Date.now();
    const random1 = Math.random().toString(36).slice(2, 11);
    const random2 = Math.random().toString(36).slice(2, 7);
    return `temp_${timestamp}_${random1}_${random2}`;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const question: ManualScreeningQuestionState = {
        id: editingQuestion?.id || generateUniqueId(),
        description: values.description.trim(),
        order: editingQuestion?.order || 0,
      };

      onSubmit(question);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={isEditing ? 'Edit Screening Question' : 'Add Screening Question'}
      open={visible}
      onCancel={handleCancel}
      maskClosable={false}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          {isEditing ? 'Save Changes' : 'Add Question'}
        </Button>,
      ]}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          name="description"
          label="Screening Question"
          rules={validationRules}
        >
          <TextArea
            placeholder="e.g., What is your expected salary range?"
            autoSize={{ minRows: 3, maxRows: 6 }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddScreeningModal;

